/**
 * Hook for AI-powered semantic slide search.
 *
 * Unlike the keyword search, this sends the query to GPT which searches
 * across all slide metadata and returns ranked results with explanations.
 *
 * Uses a request counter to guard against stale responses overwriting
 * fresh results when multiple searches are triggered in quick succession.
 */

import { useCallback, useRef, useState } from "react"

import { type AiSearchResult, aiSearchSlides } from "../api"

export interface UseAiSearchReturn {
  results: AiSearchResult[]
  summary: string
  isSearching: boolean
  /**
   * True once the latest search completed successfully (including zero results)
   * and has not been cleared. False on error so the UI does not treat a failed
   * request as an empty-result search. Distinguishes "no search yet" from
   * "searched, found nothing".
   */
  hasSearched: boolean
  error: string | null
  search: (query: string) => Promise<void>
  clear: () => void
}

export interface UseAiSearchOptions {
  /** Legacy single-folder scope. Ignored when `folderIds` is provided. */
  folderId?: string | null
  /** Multi-folder scope chips; sends as `folder_ids` to the backend. */
  folderIds?: string[]
  /** Saved-presentation scope chips; sends as `collection_ids`. */
  collectionIds?: string[]
}

export function useAiSearch(options: UseAiSearchOptions = {}): UseAiSearchReturn {
  const { folderId, folderIds, collectionIds } = options
  // Stabilise array deps for `useCallback` — callers typically pass freshly
  // constructed arrays each render, so we hash to a string key.
  const folderIdsKey = folderIds ? folderIds.join(",") : ""
  const collectionIdsKey = collectionIds ? collectionIds.join(",") : ""
  const [results, setResults] = useState<AiSearchResult[]>([])
  const [summary, setSummary] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Monotonically increasing counter to detect stale responses
  const requestIdRef = useRef(0)

  const search = useCallback(
    async (query: string) => {
      if (!query.trim()) return

      const thisRequest = ++requestIdRef.current
      setIsSearching(true)
      setError(null)

      try {
        const response = await aiSearchSlides(query.trim(), {
          folderId: folderId ?? undefined,
          folderIds: folderIdsKey ? folderIdsKey.split(",").filter(Boolean) : undefined,
          collectionIds: collectionIdsKey ? collectionIdsKey.split(",").filter(Boolean) : undefined,
        })

        // Only apply results if this is still the latest request
        if (thisRequest !== requestIdRef.current) return

        setResults(response.results)
        setSummary(response.summary)
        setHasSearched(true)
      } catch (err) {
        if (thisRequest !== requestIdRef.current) return

        const msg = err instanceof Error ? err.message : "AI search failed. Please try again."
        setError(msg)
        setResults([])
        setSummary("")
        setHasSearched(false)
      } finally {
        if (thisRequest === requestIdRef.current) {
          setIsSearching(false)
        }
      }
    },
    [folderId, folderIdsKey, collectionIdsKey]
  )

  const clear = useCallback(() => {
    requestIdRef.current++ // invalidate any in-flight request
    setResults([])
    setSummary("")
    setError(null)
    setIsSearching(false)
    setHasSearched(false)
  }, [])

  return { results, summary, isSearching, hasSearched, error, search, clear }
}
